/**
 * RichResponse — entry point for the Copilot View Protocol renderer.
 *
 *   <RichResponse view={blocks} onAction={(a) => …} />
 *
 * Renders a list of typed View Protocol blocks (see `./blocks.ts`, mirroring
 * `services-python/ai-orchestrator-service/src/copilot/view_protocol.py`)
 * into rich UI, reusing the app's existing SVG chart kit
 * (`@/routes/analytics/charts`, `@/routes/crm/charts-extra`) and display
 * components (`@/ui`). This directory is SELF-CONTAINED: it imports nothing
 * from `CopilotWidget.tsx` / `copilot-store.ts` / `types.ts` — the copilot
 * integration lane imports `RichResponse` from here and supplies `view` +
 * `onAction`.
 *
 * onAction contract: currently one action shape is emitted, fired when a
 * `form` block's submit button is used —
 *
 *   { type: 'submit_form', formId?: string, tool?: string,
 *     values: Record<string, string | boolean> }
 *
 * The integration lane is expected to route this through the copilot action
 * path (the confirm gate + `_SUBMIT_FORM_BRIDGE` / tool call on the backend),
 * the same mechanism `useCopilotForm` / `page-effects.ts` already use for a
 * page's own forms. `onAction` is optional — a `form` block still renders
 * (and its "Envoyé ✓" acknowledgement still fires) with no `onAction` wired,
 * it just has nowhere to send the values.
 *
 * Robustness: an unknown block `type` renders nothing (see `registry.tsx`'s
 * switch default); a block whose payload throws while rendering is caught by
 * a per-block error boundary and also renders nothing — a bad/partial view
 * degrades silently at any nesting depth, it never crashes the chat surface.
 */
import type { ReactNode } from 'react';

import { SafeBlock } from './registry';
import type { Block, RichAction } from './blocks';

export type { Block, RichAction } from './blocks';

export interface RichResponseProps {
  /** The `view` array from a copilot turn response (or null/undefined — a
   * text-only reply renders nothing here). */
  view: Block[] | null | undefined;
  /** Fired when an interactive block (currently: a `form` submit) produces
   * an action. Optional — see the module doc above. */
  onAction?: (action: RichAction) => void;
  className?: string;
}

export function RichResponse({ view, onAction, className = '' }: RichResponseProps): ReactNode {
  if (!Array.isArray(view) || view.length === 0) return null;
  return (
    <div className={`flex flex-col gap-4 ${className}`.trim()}>
      {view.map((block, i) => (
        <SafeBlock key={i} block={block} ctx={{ onAction, depth: 0 }} />
      ))}
    </div>
  );
}
